<script setup>
import {
  Bell,
  Building,
  Calendar,
  DollarSign,
  LayoutDashboard,
  LogOut,
  MapPin,
  Settings,
  SquareParking,
  Ticket,
  Car,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  User,
  Users,
  X
} from 'lucide-vue-next';
import { accessOwnerDashboard, accessOwnerPlusManager } from '~/lib/utils';

const props = defineProps({
  pageTitle: {
    type: String,
    required: true,
    default: 'ADD PAGE TITLE'
  }
});

const config = useRuntimeConfig();
const { getUser } = useUser();
const isOpen = ref(false);

// Desktop-only icon rail. A deliberate choice is remembered across visits.
const COLLAPSED_KEY = 'dashboard-sidebar-collapsed';
const collapsed = ref(false);
try {
  collapsed.value = localStorage.getItem(COLLAPSED_KEY) === 'true';
} catch {
  // storage unavailable (private mode, blocked): fall back to expanded
}

const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
  try {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed.value));
  } catch {
    // keep working without persistence
  }
};

// On desktop the labels disappear when collapsed; the mobile drawer always shows them
const labelClass = computed(() => (collapsed.value ? 'md:hidden' : ''));

const isAvailable = () => {
  return (
    accessOwnerPlusManager(getUser.role) && usePlan().isPlatinum)||
    (config?.public?.isMain && accessOwnerPlusManager(getUser.role)
  );
}

const route = useRoute();
const isActiveLink = (url) => {
  const path = url.split('?')[0];
  if (path === '/dashboard') return route.path === path;
  return route.path === path || route.path.startsWith(`${path}/`);
};

const navItems = ref([
  {
    title: 'Book A Spot',
    url: '/search?facility=all',
    icon: MapPin,
    isActive: !accessOwnerDashboard(getUser.role),
  },
  {
    title: 'My Reservations',
    url: '/dashboard',
    icon: Calendar,
    isActive: true,
  },
  {
    title: 'Facilities',
    url: '/dashboard/facilities',
    icon: Building,
    isActive: isAvailable(),
  },
  {
    title: 'Lots',
    url: '/dashboard/lots',
    icon: SquareParking,
    isActive: isAvailable(),
  },
  {
    title: 'Team',
    url: '/dashboard/team',
    icon: Users,
    isActive: isAvailable(),
  },
  {
    title: 'My Wallet',
    url: '/dashboard/wallet',
    icon: DollarSign,
    isActive: getUser.role === 'PARKER',
  },
  {
    title: 'My Vehicles',
    url: `/dashboard/${getUser.id}/vehicles`,
    icon: Car,
    isActive: getUser.role === 'PARKER',
  },
  {
    title: 'Discount Parking',
    url: '/parking-packs',
    icon: Ticket,
    isActive: !accessOwnerDashboard(getUser.role),
  },
]);

const toggleSidebar = () => {
  isOpen.value = !isOpen.value;
};

let resizeRafId = null

const handleResize = () => {
  if (resizeRafId) return
  resizeRafId = requestAnimationFrame(() => {
    if (window.innerWidth <= 768) {
      isOpen.value = false
    } else {
      isOpen.value = true
    }
    resizeRafId = null
  })
}

const handleLinkClick = () => {
  if (window.innerWidth < 768) {
    isOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize, { passive: true })
  isOpen.value = window.innerWidth >= 768;
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeRafId) {
    cancelAnimationFrame(resizeRafId)
    resizeRafId = null
  }
});

const logoClick = () => {
  if (getUser.role === 'PARKER') {
    navigateTo('/');
  } else {
    navigateTo('/dashboard');
  }
}

const userRole = (role) => {
  if (role === 'CPO' || role === 'PPO') return 'OWNER';
  else return role;
}
</script>

<template>
<div class="flex h-screen">
  <!-- Sidebar -->
  <div
    :class="[
      'fixed inset-y-0 left-0 z-50 w-64 transform bg-slate-950 shadow-lg transition-[transform,width] duration-300 md:relative md:translate-x-0 flex flex-col',
      isOpen ? 'w-full' : '-translate-x-full',
      collapsed ? 'md:w-[4.5rem]' : 'md:w-64'
    ]"
  >
    <button
      type="button"
      class="absolute -right-3 top-[4.5rem] z-10 hidden h-6 w-6 items-center justify-center rounded-full border bg-background text-foreground shadow-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex"
      :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      :aria-expanded="!collapsed"
      @click="toggleCollapsed"
    >
      <component :is="collapsed ? PanelLeftOpen : PanelLeftClose" class="h-3.5 w-3.5" />
    </button>
    <!-- Sidebar Header -->
    <div :class="['flex items-center justify-between p-2 bg-slate-950', collapsed && 'md:justify-center']">
      <NuxtLink class="flex items-center cursor-pointer" @click="logoClick">
        <template v-if="config?.public?.isMain">
          <img src="/images/spotsurferLogo.png" alt="Spotsurfer Logo" :class="['h-12 w-12', collapsed ? 'md:mr-0' : 'mr-1']" loading="lazy" />
          <!-- Hide when collapsed -->
          <div :class="['flex flex-col', labelClass]">
            <span class="text-3xl font-semibold text-white uppercase">
              Spotsurfer
            </span>
            <span class="text-white text-xs tracking-widest uppercase group-has-[[data-collapsible=icon]]/sidebar-wrapper:hidden">Reserve. Park. Play.</span>
          </div>
        </template>
        <template v-else>
          <NuxtImg
            :class="['max-h-16 p-2 w-full', collapsed ? 'md:max-w-12 object-contain' : 'object-cover']"
            style="min-width: 40px; max-width: 200px;"
            :src="globalConfig()?.logo?.url"
            alt="Logo"
          />
        </template>
      </NuxtLink>

      <button
        @click="toggleSidebar"
        v-if="isOpen"
        class="absolute top-2 right-2 p-2 rounded-md md:hidden text-white"
      >
        <X class="h-6 w-6" />
      </button>
    </div>

    <!-- Navigation Links -->
    <TooltipProvider :delay-duration="100">
      <nav class="p-2" aria-label="Dashboard">
        <ul class="space-y-1">
          <li v-for="(item, index) in navItems.filter((item) => item.isActive)" :key="index">
            <Tooltip :disabled="!collapsed">
              <TooltipTrigger as-child>
                <NuxtLink
                  :href="item.url"
                  :aria-label="collapsed ? item.title : undefined"
                  :aria-current="isActiveLink(item.url) ? 'page' : undefined"
                  @click="handleLinkClick(item)"
                  :class="[
                    'flex items-center rounded-md p-2 text-sm font-medium text-white transition-colors hover:text-accent-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-orange',
                    isActiveLink(item.url) && 'bg-white/10',
                    collapsed && 'md:justify-center'
                  ]"
                >
                  <component :is="item.icon" :class="['h-5 w-5 shrink-0', collapsed ? 'md:mr-0 mr-3' : 'mr-3']" />
                  <span :class="labelClass">{{ item.title }}</span>
                </NuxtLink>
              </TooltipTrigger>
              <TooltipContent side="right" :side-offset="10">{{ item.title }}</TooltipContent>
            </Tooltip>
          </li>
        </ul>
      </nav>
    </TooltipProvider>
    <div class="mt-auto">
      <span :class="['flex px-3 py-1 bg-orange-200 text-primary/80 text-xs w-[50%] font-semibold [clip-path:polygon(0_0,calc(100%-0.75rem)_0,100%_100%,0_100%)]', labelClass]">{{ userRole(getUser?.role) }}</span>
      <DropdownMenu>
        <DropdownMenuTrigger as-child  class="flex w-full items-center justify-between p-4 hover:bg-white/5">
          <button :class="collapsed && 'md:justify-center'" :aria-label="collapsed ? 'Account menu' : undefined" :title="collapsed ? (getUser?.email || 'Account') : undefined">
            <span :class="['flex h-6 w-6 shrink-0 items-center', collapsed ? 'md:mr-0 mr-2' : 'mr-2']">
              <User class="stroke-white" v-if="!getUser?.avatar" />
              <NuxtImg class="h-full w-full rounded-full object-cover" :src="getUser?.avatar" v-else />
            </span>
            <div :class="['grid flex-1 text-left text-sm leading-tight', labelClass]">
              <span class="truncate font-semibold text-white">{{ getUser?.first_name }} {{ getUser?.last_name }}</span>
              <span class="truncate text-xs text-white">{{ getUser?.email }}</span>
            </div>
            <Settings :class="['ml-2 size-4 text-white', labelClass]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="bottom" align="end" :side-offset="4">
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">{{ getUser?.first_name }} {{ getUser?.last_name }}</span>
                <span class="truncate text-xs">{{ getUser?.email }}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem class="p-0">
              <NuxtLink to="/dashboard/settings" class="flex w-full items-center p-2 text-foreground" @click="handleLinkClick">
                <Settings class="mr-2" />
                Settings
              </NuxtLink>
            </DropdownMenuItem>
            <DropdownMenuItem class="p-0">
              <NuxtLink to="/dashboard/support" class="flex w-full items-center p-2 text-foreground" @click="handleLinkClick">
                <Bell class="mr-2" />
                Support
              </NuxtLink>
            </DropdownMenuItem>
            <DropdownMenuItem v-if="getUser?.is_staff" class="p-0">
              <NuxtLink to="/admin" class="flex w-full items-center p-2 text-foreground" @click="handleLinkClick">
                <LayoutDashboard class="mr-2" />
                Admin Dashboard
              </NuxtLink>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="useLogout" class="cursor-pointer">
            <LogOut class="mr-2"/>
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>

  <!-- Main Content -->
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Top Navigation -->
    <header class="bg-white dark:bg-background z-10">
      <div class="flex justify-end px-4 items-center">
        <h1 class="p-2 text-3xl font-bold mr-auto">{{ pageTitle }}</h1>
        <button @click="toggleSidebar">
          <Menu :size="32" class="stroke-primary hover:stroke-nav-link-hover md:hidden" />
        </button>
      </div>
    </header>

    <!-- Content Area -->
    <main class="flex-1 overflow-y-auto">
      <div class="mx-auto">
        <slot />
      </div>
    </main>
  </div>

  <!-- Overlay for mobile -->
  <div
    v-if="isOpen"
    @click="toggleSidebar"
    class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
  ></div>
</div>
</template>
